'use client';

import { Button, CTA, Tag } from '@/components';
import { Element, Icon, Row } from '@vuddy/components';
import { Dropdown } from './Dropdown';
import * as styles from './optionCategoryDropdown.css';

export interface OptionCategoryDropdownOption {
  id: string | number;
  label: string;
  disabled?: boolean;
  priceLabel?: string;
  isCurrentSelection?: boolean;
  isSoldOut?: boolean;
}

interface OptionCategoryDropdownProps {
  categoryName: string;
  options: OptionCategoryDropdownOption[];
  selectedIds: (string | number)[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (id: string | number) => void;
  onComplete: () => void;
  hasParentWidth?: boolean;
  isCompleteDisabled?: boolean;
}

export const OptionCategoryDropdown = ({
  categoryName,
  options,
  selectedIds,
  open,
  onOpenChange,
  onSelect,
  onComplete,
  hasParentWidth = false,
  isCompleteDisabled,
}: OptionCategoryDropdownProps) => {
  const selectedLabels = options
    .filter(o => selectedIds.includes(o.id))
    .map(o => o.label)
    .join(', ');

  return (
    <Dropdown
      open={open}
      hasParentWidth={hasParentWidth}
      options={options.map(o => ({
        id: o.id,
        label: o.label,
        disabled: o.disabled,
        priceLabel: o.priceLabel,
        isCurrentSelection: o.isCurrentSelection,
        isSoldOut: o.isSoldOut,
      }))}
      triggerClassName={styles.trigger}
      contentClassName={styles.content}
      placement="top-start"
      offset={8}
      enableSizeMiddleware={true}
      onMenuClick={onSelect}
      closeOnOptionClick={false}
      renderOption={option => {
        const checked = selectedIds.includes(option.id);
        const disabled = option.disabled ?? false;
        return (
          <Row
            alignItems="center"
            gap="gap_base"
            className={styles.optionItem}
            aria-disabled={disabled ? 'true' : undefined}
            data-disabled={disabled ? 'true' : undefined}>
            <Icon
              name="FILL_CHECK_CIRCLE"
              size="24px"
              color={
                disabled ? 'disabled' : checked ? 'vuddyPrimary' : 'disabled'
              }
            />
            <Row
              flex={1}
              justifyContent="space-between"
              typography="bodyXsAccent"
              color={disabled ? 'disabled' : 'primary'}>
              <Row flex={1} gap="gap_xs" alignItems="center">
                <Element>{option.label}</Element>
                {option.isSoldOut && (
                  <Tag label="품절" variant="gray" size="small" />
                )}
                {option.isCurrentSelection && (
                  <span className={styles.currentSelectionTag}>
                    선택 옵션값
                  </span>
                )}
              </Row>
              {option.priceLabel != null && (
                <Element color={disabled ? 'disabled' : 'primary'}>
                  {option.priceLabel}
                </Element>
              )}
            </Row>
          </Row>
        );
      }}
      footer={setDropdownOpen => (
        <CTA direction="vertical" position="sticky">
          <Button
            variant="solidSecondary"
            fullWidth
            disabled={isCompleteDisabled ?? selectedIds.length === 0}
            onClick={() => {
              onComplete();
              setDropdownOpen(false);
            }}>
            선택 완료
          </Button>
        </CTA>
      )}>
      <Row width="100%" onClick={() => onOpenChange(!open)}>
        <Element
          flex={1}
          color={selectedLabels ? 'primary' : 'placeholder'}
          typography="bodyBase">
          {selectedLabels
            ? `(옵션) ${selectedLabels}`
            : `(옵션) ${categoryName}`}
        </Element>
        <Icon
          name="FILL_TRIANGLE_DOWN"
          size="16px"
          color={selectedLabels ? 'primary' : 'placeholder'}
        />
      </Row>
    </Dropdown>
  );
};
